import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function WhyBitmoves() {
  const { ref, isVisible } = useScrollAnimation();

  const strengths = [
    {
      id: 1,
      titleEn: 'Global standards, local reality.',
      titleJa: '世界基準を日本の現場へ翻訳する力',
      descriptionJa: 'グローバルな期待値と日本の現場条件を理解し、実現可能な形へと翻訳します。',
      descriptionEn: 'We translate global expectations into solutions that work within local realities.',
      icon: 'ri-global-line',
    },
    {
      id: 2,
      titleEn: 'Creative production from idea to launch.',
      titleJa: '構想から実装までのクリエイティブプロデュース',
      descriptionJa: 'アイデアの段階から実装・運用まで、一貫してプロデュースします。',
      descriptionEn: 'We produce end-to-end, from concept through delivery and operation.',
      icon: 'ri-lightbulb-line',
    },
    {
      id: 3,
      titleEn: 'Small team with high leverage.',
      titleJa: '小規模チームでスピードと精度を両立',
      descriptionJa: '小さなチーム構成により、迅速な意思決定と高い実行精度を実現します。',
      descriptionEn: 'Our lean structure enables fast decisions and precise execution.',
      icon: 'ri-team-line',
    },
  ];

  const clientLogos = [
    {
      id: 1,
      src: 'https://storage.googleapis.com/studio-design-asset-files/projects/7kadY9pNW3/s-382x265_webp_c3b8cde7-9f4e-4b0d-b77a-e87bc349762b.webp',
      alt: 'mirari',
    },
    {
      id: 2,
      src: 'https://storage.googleapis.com/studio-design-asset-files/projects/7kadY9pNW3/s-314x120_webp_7d7f373c-1765-4ddd-b4e1-fff0a85eaca7.webp',
      alt: 'JKFL',
    },
    {
      id: 3,
      src: 'https://storage.googleapis.com/studio-design-asset-files/projects/7kadY9pNW3/s-1280x998_v-fms_webp_db1e08a3-447c-4be6-a14d-a49c09baf342_small.webp',
      alt: 'US Air Force',
    },
    {
      id: 4,
      src: 'https://storage.googleapis.com/studio-design-asset-files/projects/7kadY9pNW3/s-407x288_webp_a3d526fc-3d00-4995-aea4-7336669e347e.webp',
      alt: 'Target',
    },
    {
      id: 5,
      src: 'https://storage.googleapis.com/studio-design-asset-files/projects/7kadY9pNW3/s-600x258_v-fs_webp_73309328-f2ca-4438-9c73-0fc35c070c0d_small.webp',
      alt: 'favi',
    },
    {
      id: 6,
      src: 'https://storage.googleapis.com/studio-design-asset-files/projects/7kadY9pNW3/s-300x300_webp_dd985d5f-f5be-48a9-8ae1-665832ef045d.webp',
      alt: 'BPYO',
    },
  ];

  return (
    <section
      ref={ref}
      id="why-bitmoves"
      className="bg-white py-36 lg:py-44 px-8 lg:px-32"
    >
      <div className="max-w-7xl mx-auto">
        <div
          className={`mb-20 flex items-baseline gap-6 transition-all duration-700 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-5xl lg:text-6xl font-medium text-gray-900">
            Why BITMOVES
          </h2>
          <span className="text-base text-gray-500">私たちの強み</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-32">
          {strengths.map((strength, index) => (
            <div
              key={strength.id}
              className={`group transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: isVisible
                  ? `${index * 150 + 200}ms`
                  : '0ms',
              }}
            >
              <div className="w-16 h-16 flex items-center justify-center mb-8 bg-gray-50 rounded-2xl group-hover:bg-gray-900 transition-colors duration-300">
                <i
                  className={`${strength.icon} text-3xl text-gray-900 group-hover:text-white transition-colors duration-300`}
                ></i>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-4 leading-relaxed">
                {strength.titleJa}
              </h3>
              <p className="text-base text-gray-900 font-medium mb-3 leading-relaxed">
                {strength.titleEn}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                {strength.descriptionJa}
              </p>
              <p className="text-sm text-gray-500 leading-relaxed">
                {strength.descriptionEn}
              </p>
            </div>
          ))}
        </div>

        {/* Clients & Partners Section */}
        <div
          className={`transition-all duration-700 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
          style={{
            transitionDelay: isVisible ? '600ms' : '0ms',
          }}
        >
          <h2 className="text-4xl lg:text-5xl font-medium text-gray-900 mb-16 text-center">
            Clients & Partners
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12 items-center">
            {clientLogos.map((logo, index) => (
              <div
                key={logo.id}
                className={`flex items-center justify-center transition-all duration-700 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: isVisible
                    ? `${index * 100 + 700}ms`
                    : '0ms',
                }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="w-full h-auto max-h-20 object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
